type FormatCurrencyInputs = {
  value: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

export function formatCurrency({
  value,
  minimumFractionDigits = 0,
  maximumFractionDigits = 0,
}: FormatCurrencyInputs) {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits,
  });
}

export function formatCalculatorResultsGraphBarTopCurrency(
  value: number,
  useCompactSuffixes: boolean,
): string {
  let desiredValue = formatCurrency({ value });
  const valueAsMillions = Math.round(value / 1_000_000);
  const valueAsBillions = valueAsMillions / 1000;

  if (useCompactSuffixes && Math.floor(valueAsBillions) > 0) {
    desiredValue = `${formatCurrency({
      value: valueAsBillions,
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    })}B`;
  } else if (useCompactSuffixes && valueAsMillions > 0) {
    desiredValue = `${formatCurrency({
      value: valueAsMillions,
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    })}M`;
  }

  return desiredValue;
}

export type FormatCalculatorResultsGraphYAxisTickOptions = {
  /**
   * When set (including 0), the thousands branch uses `value / 1000` with this digit cap instead of
   * `floor(value / 1000)` — needed so evenly spaced ticks (e.g. $1K and $1.5K) get distinct labels.
   * Omit for compound-calculator behavior (floor K only).
   */
  maximumFractionDigits?: number;
};

/**
 * Y-axis tick labels for calculator results bar charts (compound interest and CD summary).
 * Matches compound calculator `CalculatorResultsGraph`: K uses `floor(value / 1000)`; M/B use scaled
 * values with up to three fraction digits.
 */
export function formatCalculatorResultsGraphYAxisTickAsCurrency(
  value: number,
  options?: FormatCalculatorResultsGraphYAxisTickOptions,
): string {
  const formattedAsThousands = Math.floor(value / 1000);
  let desiredValue = formatCurrency({ value });
  const valueAsMillions = Math.round(value / 1_000_000);
  const valueAsBillions = valueAsMillions / 1000;

  if (Math.floor(valueAsBillions) > 0) {
    desiredValue = `${formatCurrency({
      value: valueAsBillions,
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    })}B`;
  } else if (Math.floor(valueAsMillions) > 0) {
    desiredValue = `${formatCurrency({
      value: valueAsMillions,
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    })}M`;
  } else if (formattedAsThousands > 0) {
    if (options?.maximumFractionDigits !== undefined) {
      const k = value / 1000;
      desiredValue = `${formatCurrency({
        value: k,
        minimumFractionDigits: 0,
        maximumFractionDigits: options.maximumFractionDigits,
      })}K`;
    } else {
      desiredValue = `${formatCurrency({
        value: formattedAsThousands,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}K`;
    }
  } else {
    desiredValue = `${formatCurrency({ value: value })}`;
  }
  return desiredValue;
}

const MAX_AXIS_TICK_DECIMAL_DIGITS = 3;

/**
 * Builds axis labels with {@link formatCalculatorResultsGraphYAxisTickAsCurrency}; when two tick **values**
 * would share the same string (e.g. both `$1K` at $1,000 and $1,500 from floor-K formatting), bumps
 * fractional digits on those ticks until labels differ (for CD charts with explicit evenly spaced ticks).
 */
export function getCalculatorResultsGraphYAxisTickLabelsResolvingDuplicates(
  ticks: readonly number[],
): string[] {
  const n = ticks.length;
  /** 0 = compound floor-K; &gt; 0 = pass as `maximumFractionDigits` for the K branch. */
  const precision = Array.from({ length: n }, () => 0);

  const labels = () =>
    ticks.map((t, i) =>
      precision[i] === 0
        ? formatCalculatorResultsGraphYAxisTickAsCurrency(t)
        : formatCalculatorResultsGraphYAxisTickAsCurrency(t, {
            maximumFractionDigits: precision[i],
          }),
    );

  for (let iter = 0; iter < 32; iter += 1) {
    const lbl = labels();
    const byLabel = new Map<string, number[]>();
    lbl.forEach((s, i) => {
      const arr = byLabel.get(s) ?? [];
      arr.push(i);
      byLabel.set(s, arr);
    });

    let bumped = false;
    for (const indices of byLabel.values()) {
      if (indices.length < 2) {
        continue;
      }
      const distinctVals = new Set(indices.map(i => ticks[i]));
      if (distinctVals.size <= 1) {
        continue;
      }
      for (const i of indices) {
        if (precision[i] < MAX_AXIS_TICK_DECIMAL_DIGITS) {
          precision[i]++;
          bumped = true;
        }
      }
    }

    if (!bumped) {
      break;
    }
  }

  return labels();
}

/** Strip commas before validating / storing currency field values. */
export function stripCommaGroups(raw: string): string {
  return raw.replace(/,/g, '');
}
